import React, { useEffect, useState, useRef } from "react";
import {
  Box, Button, IconButton, Tooltip, Typography, Dialog,
  DialogTitle, DialogContent, useTheme, Input, Stack,
  TextField
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PrintIcon from "@mui/icons-material/Print";
import InfoIcon from "@mui/icons-material/Info";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import DownloadIcon from "@mui/icons-material/Download";
import UploadIcon from "@mui/icons-material/Upload";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";

import mammoth from "mammoth";
import * as XLSX from "xlsx";
import Papa from "papaparse";
import * as pdfjsLib from "pdfjs-dist";
import * as UTIF from "utif";
import "../styles/BlobViewer.css";
import API from "../api/axiosInstance";

const BlobViewer: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const textContainerRef = useRef<HTMLDivElement | null>(null);

  const { fieldLabel, blobData, mimeType, fileID } = location.state || {};

  const [blobUrl, setBlobUrl] = useState("");
  const [fileName, setFileName] = useState(fieldLabel || "file");
  const [fileSize, setFileSize] = useState<number | null>(null);
  const [zoom, setZoom] = useState(1);
  const [pageIndex, setPageIndex] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [showInfo, setShowInfo] = useState(false);
  const [fileContent, setFileContent] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [replaceFile, setReplaceFile] = useState<File | null>(null);
  const [highlightedHtml, setHighlightedHtml] = useState("");
  const [csvData, setCsvData] = useState<string[][]>([]);
  const [resolvedMimeType, setResolvedMimeType] = useState<string | null>(mimeType ?? null);
  const [error, setError] = useState<string | null>(null);


  useEffect(() => {
    if (fileID) {
      // Load file from API
      fetchBlobFromAPI(fileID);
    } else if (blobData && mimeType) {
      decodeBlob(blobData, mimeType);
    }
  }, [blobData, mimeType, fileID, pageIndex]);

  const fetchBlobFromAPI = async (id: string) => {
    try {
      const isBlob = location.state?.isBlob;
      const user = JSON.parse(localStorage.getItem('user') || "{}");
      const headers = {
          headers: {
              authToken: user.user?.authtoken,
              sessionId: user.user?.sessionId,
          },
          responseType: "blob",
      };
      const res = await API.get(`/storage/${isBlob ? "blob" : "file"}/${id}`,headers);
      const blob = res.data();
      const dataUrl = await convertBlobToDataUrl(blob);
      setResolvedMimeType(blob.type); 
      decodeBlob(dataUrl, blob.type);
    } catch (err) {
        // inside catch block:
        setError("Failed to load file.");

      console.error("Failed to load blob from API:", err);
    }
  };

  
  const convertBlobToDataUrl = (blob: Blob): Promise<string> => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.readAsDataURL(blob);
    });
  };

  const decodeBlob = async (dataUrl: string, type: string) => {
    try {
      const byteString = atob(dataUrl.split(",")[1]);
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      for (let i = 0; i < byteString.length; i++) ia[i] = byteString.charCodeAt(i);

      const blob = new Blob([ab], { type });
      setFileSize(blob.size);
      const url = URL.createObjectURL(blob);
      setBlobUrl(url);

      if (type.startsWith("text") || fileName.endsWith(".txt")) {
        const text = await blob.text();
        setFileContent(text);
      } else if (type.includes("wordprocessingml.document")) {
        const buffer = await blob.arrayBuffer();
        const result = await mammoth.convertToHtml({ arrayBuffer: buffer });
        setFileContent(result.value);
      } else if (type.includes("spreadsheetml")) {
        const buffer = await blob.arrayBuffer();
        const workbook = XLSX.read(buffer, { type: "array" });
        const csv = XLSX.utils.sheet_to_csv(workbook.Sheets[workbook.SheetNames[0]]);
        const parsed = Papa.parse(csv).data as string[][];
        setCsvData(parsed);
      } else if (type.includes("tiff")) {
        const buffer = await blob.arrayBuffer();
        const ifds = UTIF.decode(buffer);
        const selectedIFD = ifds[pageIndex] || ifds[0];
        
        UTIF.decodeImage(buffer, selectedIFD);
        const rgba = UTIF.toRGBA8(selectedIFD);
        
        const { width, height } = selectedIFD;
        const canvas = canvasRef.current;
        if (canvas) {
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");
          if (ctx) {
            const imageData = ctx.createImageData(width, height);
            imageData.data.set(rgba);
            ctx.putImageData(imageData, 0, 0);
          }
        }
        
      } else if (type === "application/pdf") {
        const pdf = await pdfjsLib.getDocument({ url }).promise;
        setTotalPages(pdf.numPages);
      }

    } catch (err) {
      console.error("Failed to decode blob:", err);
    }
  };

  const handlePrint = () => {
    const iframe = document.createElement("iframe");
    iframe.style.display = "none";
    iframe.src = blobUrl;
    document.body.appendChild(iframe);
    iframe.onload = () => iframe.contentWindow?.print();
  };

  const handleDownload = () => {
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = fileName;
    a.click();
  };

  const isTextBased = mimeType?.startsWith("text") || fileName.endsWith(".txt");
  const isCSV = mimeType?.includes("csv") || fileName.endsWith(".csv");
  const isDOCX = mimeType?.includes("wordprocessingml.document");
  const isXLSX = mimeType?.includes("spreadsheetml.sheet");
  const isImage = mimeType?.startsWith("image/");
  const isPDF = mimeType === "application/pdf";
  const isTIF = mimeType?.includes("tiff");

  const handleSearch = () => {
    if (fileContent && searchTerm) {
      const escaped = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const re = new RegExp(escaped, "gi");
      const highlighted = fileContent.replace(re, match => `<mark>${match}</mark>`);
      setHighlightedHtml(highlighted);
    }
  };

  const renderViewer = () => {
    if (!blobUrl) {
      return (
        <Typography className="blob-message" color="error">
          Unable to load file.
        </Typography>
      );
    }

    if (isImage && !isTIF) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" flexGrow={1}>
          <img
            src={blobUrl}
            alt="Image Preview"
            style={{
              transform: `scale(${zoom})`,
              maxWidth: "100%",
              maxHeight: "calc(100vh - 150px)",
              objectFit: "contain"
            }}
          />
        </Box>
      );
    }

    if (isTIF) {
      return (
        <Box display="flex" justifyContent="center" mt={2}>
          <canvas ref={canvasRef} style={{ maxWidth: "100%" }} />
        </Box>
      );
    }

    if (isPDF) {
      return (
        <iframe
          src={blobUrl}
          title="PDF Viewer"
          style={{
            width: "100%",
            height: "calc(100vh - 160px)",
            border: "none"
          }}
        />
      );
    }

    if (isDOCX) {
      return (
        <Box
          className="docx-preview"
          dangerouslySetInnerHTML={{ __html: highlightedHtml || fileContent }}
          sx={{
            px: 2,
            py: 1,
            overflowY: "auto",
            maxHeight: "calc(100vh - 160px)"
          }}
        />
      );
    }

    if (isXLSX || isCSV) {
      return (
        <Box sx={{ overflow: "auto", maxHeight: "calc(100vh - 160px)" }}>
          <table className="csv-table">
            <thead>
              <tr>
                {csvData[0]?.map((cell, idx) => <th key={idx}>{cell}</th>)}
              </tr>
            </thead>
            <tbody>
              {csvData.slice(1).map((row, rIdx) => (
                <tr key={rIdx}>
                  {row.map((cell, cIdx) => (
                    <td key={cIdx}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
      );
    }

    if (isTextBased) {
      return (
        <Box
          ref={textContainerRef}
          className="text-preview"
          sx={{
            px: 2,
            py: 1,
            overflowY: "auto",
            whiteSpace: "pre-wrap",
            fontFamily: "monospace",
            maxHeight: "calc(100vh - 160px)",
            borderRadius: 1,
            bgcolor: theme.palette.mode === "dark" ? "#1e1e1e" : "#f8f8f8"
          }}
        >
          <Typography component="div" dangerouslySetInnerHTML={{ __html: highlightedHtml || fileContent }} />
        </Box>
      );
    }

    return (
      <Typography className="blob-message" color="warning.main">
        This file type cannot be previewed.
      </Typography>
    );
  };

  return (
    <Box className="blob-viewer-container" sx={{ p: 2, pt: 10 }}>
      {/* Toolbar */}
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Back">
            <IconButton onClick={() => navigate(-1)} color="primary">
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Print">
            <IconButton onClick={handlePrint} color="primary" disabled={!blobUrl}>
              <PrintIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Download">
            <IconButton onClick={handleDownload} color="primary" disabled={!blobUrl}>
              <DownloadIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Replace File">
            <IconButton component="label" color="primary">
              <UploadIcon />
              <Input
                type="file"
                hidden
                onChange={(e) => {
                    const input = e.target as HTMLInputElement;
                    setReplaceFile(input.files?.[0] || null);
                }}
                />
            </IconButton>
          </Tooltip>

            {error && (
            <Typography color="error" variant="body2" sx={{ mb: 2 }}>
                {error}
            </Typography>
            )}

          {(isImage || isTIF) && (
            <>
              <Tooltip title="Zoom In">
                <IconButton onClick={() => setZoom((z) => Math.min(z + 0.25, 3))}>
                  <ZoomInIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Zoom Out">
                <IconButton onClick={() => setZoom((z) => Math.max(z - 0.25, 0.25))}>
                  <ZoomOutIcon />
                </IconButton>
              </Tooltip>
            </>
          )}

          {(isPDF || isTIF) && totalPages > 1 && (
            <>
              <Tooltip title="Previous Page">
                <IconButton onClick={() => setPageIndex((p) => Math.max(0, p - 1))}>
                  <NavigateBeforeIcon />
                </IconButton>
              </Tooltip>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Page {pageIndex + 1} of {totalPages}
              </Typography>
              <Tooltip title="Next Page">
                <IconButton onClick={() => setPageIndex((p) => Math.min(p + 1, totalPages - 1))}>
                  <NavigateNextIcon />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center">
          {(fileContent || isDOCX || isTextBased) && (
            <>
              <TextField
                size="small"
                placeholder="Search text..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{ width: 200 }}
              />
              <Button onClick={handleSearch} variant="outlined">Search</Button>
            </>
          )}
          <Tooltip title="File Info">
            <IconButton onClick={() => setShowInfo(true)} color="primary">
              <InfoIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {/* Viewer Panel */}
      <Box className="blob-preview">{renderViewer()}</Box>

      {/* File Info Dialog */}
      <Dialog open={showInfo} onClose={() => setShowInfo(false)} maxWidth="sm" fullWidth>
        <DialogTitle>File Info</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1"><strong>Name:</strong> {fileName}</Typography>
          <Typography variant="body1"><strong>Type:</strong> {mimeType}</Typography>
          <Typography variant="body1"><strong>Size:</strong> {fileSize?.toLocaleString()} bytes</Typography>
          {totalPages > 1 && (
            <Typography variant="body1"><strong>Pages:</strong> {totalPages}</Typography>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default BlobViewer;
